import { existsSync } from 'fs';
import { join } from 'path';
import { FrameworkInfo } from './framework-detector';
import { getErrorMessage } from './error-type-guards';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export async function validateProjectStructure(framework: FrameworkInfo): Promise<ValidationResult> {
  const errors: string[] = [];
  const warnings: string[] = [];
  const cwd = process.cwd();

  // Check for Next.js config
  if (!existsSync(join(cwd, 'next.config.js')) && 
      !existsSync(join(cwd, 'next.config.mjs')) && 
      !existsSync(join(cwd, 'next.config.ts'))) {
    warnings.push('No next.config file found');
  }

  // Check for conflicting configs
  if (existsSync(join(cwd, 'vibeship.config.ts')) || 
      existsSync(join(cwd, 'vibeship.config.js'))) {
    warnings.push('Existing vibeship.config file found - it will be overwritten');
  }

  // Check API routes directory
  const apiPath = framework.isAppRouter 
    ? join(cwd, framework.hasSrcDir ? 'src/app/api' : 'app/api')
    : join(cwd, framework.hasSrcDir ? 'src/pages/api' : 'pages/api');

  if (existsSync(join(apiPath, 'vibeship'))) {
    warnings.push('Existing /api/vibeship routes found - they will be overwritten');
  }

  // Check for required directories
  if (framework.isAppRouter) {
    const appDir = framework.hasSrcDir ? 'src/app' : 'app';
    if (!existsSync(join(cwd, appDir))) {
      errors.push(`App directory not found at ${appDir}`);
    }
  } else {
    const pagesDir = framework.hasSrcDir ? 'src/pages' : 'pages';
    if (!existsSync(join(cwd, pagesDir))) {
      errors.push(`Pages directory not found at ${pagesDir}`);
    }
  }

  // Check for layout file (App Router)
  if (framework.isAppRouter) {
    const layoutPath = framework.hasSrcDir ? 'src/app/layout' : 'app/layout';
    const hasLayout = existsSync(join(cwd, `${layoutPath}.tsx`)) || 
                      existsSync(join(cwd, `${layoutPath}.jsx`)) ||
                      existsSync(join(cwd, `${layoutPath}.js`));
    
    if (!hasLayout) {
      warnings.push('No layout file found - you\'ll need to add VibecodeDevtools manually');
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

export function validateConfigFile(configPath: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!existsSync(configPath)) {
    errors.push('Configuration file not found');
    return { isValid: false, errors, warnings };
  }

  try {
    const content = readFileSync(configPath, 'utf-8');
    
    // Basic validation checks
    if (!content.includes('defineConfig')) {
      errors.push('Invalid config file - missing defineConfig');
    }

    if (!content.includes('scanPaths')) {
      warnings.push('No scanPaths defined - using defaults');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  } catch (error) {
    errors.push(`Failed to read config file: ${getErrorMessage(error)}`);
    return { isValid: false, errors, warnings };
  }
}

// Add missing import
import { readFileSync } from 'fs';